import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { fetchOnboarding, fetchOrderStatus, submitOnboarding, uploadReference } from "@/lib/api";
import { Seo } from "@/components/Seo";

const EASE = [0.16, 1, 0.3, 1];
const money = (cents) => `$${(cents / 100).toLocaleString()}`;
const inputCls =
  "w-full border border-white/15 bg-transparent px-4 py-3.5 text-sm text-white placeholder:text-fog/40 outline-none transition-colors duration-300 focus:border-white/60";

function Field({ label, optional, children }) {
  return (
    <label className="block">
      <span className="text-[10px] font-medium uppercase tracking-[0.3em] text-fog/70">
        {label}
        {optional && <span className="ml-2 text-fog/40">optional</span>}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

export default function Onboard() {
  const { token: pathToken } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const tokenParam = pathToken || params.get("token");
  const [order, setOrder] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | done | missing
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(0);
  const fileInputRef = useRef(null);
  const attempts = useRef(0);
  const [form, setForm] = useState({
    business_name: "", category: "", services: "", existing_website: "",
    service_area: "", changes: "", additional: "",
  });
  const [assets, setAssets] = useState([]);
  const tokenRef = useRef(tokenParam || null);

  useEffect(() => {
    let cancelled = false;
    const loadByToken = async (token) => {
      try {
        const { data } = await fetchOnboarding(token);
        if (cancelled) return;
        tokenRef.current = token;
        setOrder(data);
        setForm((f) => ({
          ...f,
          ...Object.fromEntries(Object.entries(data.business || {}).map(([k, v]) => [k, v || ""])),
        }));
        setAssets(data.assets || []);
        setState(data.onboarding_complete ? "done" : "ready");
      } catch {
        if (!cancelled) setState("missing");
      }
    };
    if (tokenParam) {
      loadByToken(tokenParam);
      return undefined;
    }
    if (!sessionId) {
      setState("missing");
      return undefined;
    }
    // Arriving from Stripe: verify payment, then trade the session for the
    // secure onboarding token released by the server only once paid.
    const poll = async () => {
      try {
        const { data } = await fetchOrderStatus(sessionId);
        if (cancelled) return;
        if (data.payment_status === "paid" && data.onboarding_token) {
          loadByToken(data.onboarding_token);
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("missing");
          return;
        }
      } catch {
        if (cancelled) return;
      }
      attempts.current += 1;
      if (attempts.current < 25) setTimeout(poll, 2000);
      else setState("missing");
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, tokenParam]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const focusScroll = (e) => setTimeout(() => e.target.scrollIntoView({ behavior: "smooth", block: "center" }), 320);

  const onFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    for (const f of files) {
      setUploading((n) => n + 1);
      try {
        const { data } = await uploadReference(f);
        setAssets((prev) => [...prev, { path: data.path, filename: data.filename }]);
      } catch {
        toast.error(`Couldn't upload ${f.name}.`);
      } finally {
        setUploading((n) => n - 1);
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const submit = async () => {
    if (!form.business_name.trim()) {
      toast.error("Your business name is the one thing we need.");
      return;
    }
    setSubmitting(true);
    try {
      await submitOnboarding(tokenRef.current, { ...form, assets });
      setState("done");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const optionLabel = order
    ? order.purchase_type === "own" ? "Own It — one-time" : "Website as a Service — monthly"
    : "";

  return (
    <div className="min-h-screen bg-obsidian text-white">
      <Seo title="Welcome" noindex path="/onboard" />
      <div className="mx-auto max-w-2xl px-5 pb-32 pt-32 md:px-10 md:pt-44">
        {state === "loading" && (
          <div data-testid="onboard-loading">
            <p className="text-[10px] font-medium uppercase tracking-[0.5em] text-fog md:text-xs">Payment</p>
            <h1 className="mt-4 font-display text-4xl font-medium leading-[1.02] tracking-[-0.02em] md:text-6xl">
              Confirming your payment…
            </h1>
            <p className="mt-6 max-w-lg text-sm leading-relaxed text-fog md:text-base">
              We're verifying with Stripe — this takes a few seconds.
            </p>
          </div>
        )}

        {state === "missing" && (
          <div data-testid="onboard-missing">
            <p className="text-[10px] font-medium uppercase tracking-[0.5em] text-fog md:text-xs">Onboarding</p>
            <h1 className="mt-4 font-display text-4xl font-medium leading-[1.02] tracking-[-0.02em] md:text-6xl">
              This link isn't active.
            </h1>
            <p className="mt-6 max-w-lg text-sm leading-relaxed text-fog md:text-base">
              Onboarding links arrive in your confirmation email after payment. If you believe this is a
              mistake, start a project conversation and we'll find your order.
            </p>
            <Link to="/work" data-testid="onboard-missing-work-link" className="group mt-10 inline-flex items-center gap-2 border border-white px-6 py-4 text-xs font-semibold uppercase tracking-[0.25em] transition-colors duration-500 hover:bg-white hover:text-obsidian">
              Back to the showroom
              <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-0.5" />
            </Link>
          </div>
        )}

        {(state === "ready" || state === "done") && order && (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }} data-testid="onboard-ready">
            <div className="flex h-14 w-14 items-center justify-center border border-white/20">
              <Check className="h-6 w-6" />
            </div>
            <p className="mt-10 text-[10px] font-medium uppercase tracking-[0.5em] text-fog md:text-xs">
              Order confirmed — {order.order_number}
            </p>
            <h1 className="mt-4 font-display text-4xl font-medium leading-[1.02] tracking-[-0.02em] md:text-6xl">
              You're in. Let's make it yours.
            </h1>
            <div className="mt-10 border border-white/15 p-6 md:p-8" data-testid="onboard-summary">
              <div className="flex items-baseline justify-between gap-4">
                <p className="font-display text-2xl font-medium tracking-[-0.02em]" data-testid="onboard-product">{order.product_name}</p>
                <p className="font-display text-3xl font-medium tracking-[-0.02em]" data-testid="onboard-amount">
                  {money(order.amount_cents)}
                  <span className="ml-2 text-sm font-normal text-fog">{order.purchase_type === "waas" ? "/ month" : "one time"}</span>
                </p>
              </div>
              <p className="mt-1 text-xs uppercase tracking-[0.3em] text-fog" data-testid="onboard-option">{optionLabel}</p>
              {order.contact?.email && (
                <p className="mt-5 border-t border-white/10 pt-5 text-sm text-fog" data-testid="onboard-contact">
                  {order.contact.name ? `${order.contact.name} · ` : ""}{order.contact.email}
                </p>
              )}
            </div>

            {state === "done" ? (
              <div className="mt-10" data-testid="onboard-done">
                <p className="max-w-lg text-sm leading-relaxed text-fog md:text-base">
                  {order.onboarding_complete
                    ? "Your details are in. Findley St will review everything and follow up about customization and launch."
                    : "Saved."}
                </p>
                <Link to="/work" data-testid="onboard-done-work-link" className="group mt-8 inline-flex items-center gap-2 border border-white px-6 py-4 text-xs font-semibold uppercase tracking-[0.25em] transition-colors duration-500 hover:bg-white hover:text-obsidian">
                  Back to the showroom
                  <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-0.5" />
                </Link>
              </div>
            ) : (
              <div className="mt-12 space-y-6" data-testid="onboard-form">
                <Field label="Business name">
                  <input data-testid="onboard-business" className={inputCls} value={form.business_name} onFocus={focusScroll} onChange={(e) => set("business_name", e.target.value)} />
                </Field>
                <div className="grid gap-6 md:grid-cols-2">
                  <Field label="Business category" optional>
                    <input data-testid="onboard-category" className={inputCls} value={form.category} onFocus={focusScroll} onChange={(e) => set("category", e.target.value)} />
                  </Field>
                  <Field label="Location / service area" optional>
                    <input data-testid="onboard-area" className={inputCls} value={form.service_area} onFocus={focusScroll} onChange={(e) => set("service_area", e.target.value)} />
                  </Field>
                </div>
                <Field label="Existing website" optional>
                  <input data-testid="onboard-website" type="url" placeholder="https://" className={inputCls} value={form.existing_website} onFocus={focusScroll} onChange={(e) => set("existing_website", e.target.value)} />
                </Field>
                <Field label="Services" optional>
                  <textarea data-testid="onboard-services" rows={3} placeholder="What you offer, in your own words" className={inputCls} value={form.services} onFocus={focusScroll} onChange={(e) => set("services", e.target.value)} />
                </Field>
                <Field label="What should change for your business?" optional>
                  <textarea data-testid="onboard-changes" rows={3} className={inputCls} value={form.changes} onFocus={focusScroll} onChange={(e) => set("changes", e.target.value)} />
                </Field>
                <Field label="Anything else" optional>
                  <textarea data-testid="onboard-additional" rows={2} className={inputCls} value={form.additional} onFocus={focusScroll} onChange={(e) => set("additional", e.target.value)} />
                </Field>
                <div>
                  <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf" className="hidden" onChange={(e) => onFiles(e.target.files)} data-testid="onboard-assets-input" />
                  <button type="button" onClick={() => fileInputRef.current?.click()} data-testid="onboard-assets-button" className="group inline-flex items-center gap-2 border border-white/15 px-5 py-3 text-xs uppercase tracking-[0.25em] text-fog transition-colors hover:border-white/50 hover:text-white">
                    <Paperclip className="h-3.5 w-3.5" />
                    {uploading ? `Uploading ${uploading}…` : "Logo or photos (optional)"}
                  </button>
                  {assets.length > 0 && (
                    <ul className="mt-3 space-y-2" data-testid="onboard-assets-list">
                      {assets.map((a) => (
                        <li key={a.path} className="flex items-center justify-between border border-white/10 px-4 py-2.5 text-xs text-fog">
                          {a.filename}
                          <button type="button" onClick={() => setAssets((p) => p.filter((x) => x.path !== a.path))} aria-label={`Remove ${a.filename}`}>
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <button
                  type="button"
                  onClick={submit}
                  disabled={submitting}
                  data-testid="onboard-submit"
                  className="group flex w-full items-center justify-center gap-3 border border-white bg-white px-6 py-5 text-xs font-semibold uppercase tracking-[0.25em] text-obsidian transition-colors duration-500 ease-cinematic hover:bg-transparent hover:text-white disabled:opacity-50"
                >
                  {submitting ? "Saving…" : "Save and continue"}
                  <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-0.5" />
                </button>
                <p className="text-center text-xs leading-relaxed text-fog/50">
                  In a hurry? You can finish this anytime — the same secure link is in your confirmation email.
                </p>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
